/**
 * ST-04 Anaphoric parallelism (INV-CH-ST-04; changes C-D1-ST04, C-D2-ST04).
 *
 * The seed's algorithm: every prose sentence has up to two opening-frame keys (A: words 1-2,
 * B: words 2-3, lower-cased, non-letters stripped). A key that opens three or more sentences within
 * a 7-sentence window is a run of hits h_0 < h_1 < …; it contributes `hits − 1` units. Runs never
 * cross a section start (scene, else chapter, else document; ENGINE-SPEC §2.8a): the window of a
 * sentence stops at the end of its section.
 *
 * Attribution: each repeat (every hit after the first) is one unit and goes to the paragraph that
 * contains that sentence; the first hit is the anchor and is shown as evidence with weight 0.
 * Exposure: prose sentences. The seed's count ramp is removed (D1); the rate ramp gets the seed's
 * zero guard 12 prepended as (0, 12) (D2).
 */
import {
	line,
	nativeMeasure,
	quote,
	rateDef,
	sentenceExposure,
	wordSentences,
} from './a-shared.ts';
import { makeEvidence, rateChannel } from './framework.ts';
import { proseSentenceList } from './helpers.ts';
import type { ChannelImpl, Measurement, RunContext } from './types.ts';

const WINDOW = 7;
const NON_WORD = /[^a-zÀ-ɏ\s'’-]/g;
const TOKEN = /\S+/g;

export const st04Def = rateDef({
	id: 'ST-04',
	name: 'Anaphoric parallelism',
	family: 'CHOREO',
	features: [
		{ id: 'frameRepeat', label: 'Repeated sentence frame' },
		{ id: 'frameAnchor', label: 'First sentence of a frame run (evidence only)' },
	],
	oneWay: true,
	minExposure: 6,
	exposureUnit: 'sentences',
	lang: 'native',
	ramp: [
		[0, 12],
		[0.05, 35],
		[0.1, 55],
		[0.2, 74],
		[0.35, 88],
	],
	unitScale: 1,
	unitLabel: 'frame repeats per sentence',
	phi0: 1.5,
	weight: 1.2,
	nd: 0.3,
	changes: ['C-D1-ST04', 'C-D2-ST04'],
});

interface Toks {
	words: string[];
	/** Offsets of each word in the sentence text. */
	at: number[];
}

function tokenise(s: string): Toks {
	const cleaned = s.toLowerCase().replace(NON_WORD, ' ');
	const words: string[] = [];
	const at: number[] = [];
	TOKEN.lastIndex = 0;
	for (let m = TOKEN.exec(cleaned); m !== null; m = TOKEN.exec(cleaned)) {
		words.push(m[0]);
		at.push(m.index);
	}
	return { words, at };
}

function measure(ctx: RunContext): Measurement {
	const { doc } = ctx;
	const sl = proseSentenceList(doc);
	const list = wordSentences(doc); // sentences with at least one word, as prose-list indices
	const n = list.length;
	const ids = new Map<string, number>();
	const keyId = (key: string): number => {
		let id = ids.get(key);
		if (id === undefined) {
			id = ids.size;
			ids.set(key, id);
		}
		return id;
	};
	const keyName: string[] = [];
	const A = new Int32Array(n).fill(-1);
	const B = new Int32Array(n).fill(-1);
	const S = doc.sentences;
	for (let r = 0; r < n; r++) {
		const d = sl.sent[list[r] as number] as number;
		const t = tokenise(doc.text.slice(S.charStart[d] as number, S.charEnd[d] as number));
		const w = t.words;
		if (w.length >= 2) {
			A[r] = keyId(`A:${w[0]} ${w[1]}`);
			keyName[A[r] as number] = `A:${w[0]} ${w[1]}`;
		}
		if (w.length >= 3) {
			B[r] = keyId(`B:${w[1]} ${w[2]}`);
			keyName[B[r] as number] = `B:${w[1]} ${w[2]}`;
		}
	}
	const k = new Float64Array(doc.paragraphs.count);
	const e = sentenceExposure(doc);
	const ev = makeEvidence();
	const summary: string[] = [];
	const seen = new Set<number>();
	const span = (r: number, key: number, weight: number, feature: number): void => {
		const d = sl.sent[list[r] as number] as number;
		const start = S.charStart[d] as number;
		const t = tokenise(doc.text.slice(start, S.charEnd[d] as number));
		const first = keyName[key]?.startsWith('A:') ? 0 : 1;
		const a = t.at[first];
		const last = t.at[first + 1];
		if (a === undefined || last === undefined) return;
		const lw = t.words[first + 1] as string;
		ev.add(start + a, start + last + lw.length, feature, weight);
	};
	const paraOf = (r: number): number => sl.para[list[r] as number] as number;
	const secOf = (r: number): number => sl.secStart[list[r] as number] as number;
	const hasKey = (r: number, key: number): boolean => A[r] === key || B[r] === key;
	for (let i = 0; i < n; i++) {
		for (const key of [A[i] as number, B[i] as number]) {
			if (key < 0) continue;
			const mark = key * (n + 1) + i;
			if (seen.has(mark)) continue;
			const hits = [i];
			const lim = Math.min(n, i + WINDOW);
			const sec = secOf(i);
			for (let j = i + 1; j < lim && secOf(j) === sec; j++) if (hasKey(j, key)) hits.push(j);
			if (hits.length < 3) continue;
			for (const h of hits) seen.add(key * (n + 1) + h);
			for (let h = 0; h < hits.length; h++) {
				const r = hits[h] as number;
				if (h === 0) span(r, key, 0, 1);
				else {
					k[paraOf(r)] = (k[paraOf(r)] as number) + 1;
					span(r, key, 1, 0);
				}
			}
			if (summary.length < 6)
				summary.push(
					line(
						`${quote(`…${(keyName[key] as string).slice(2)}…`)} ×${hits.length}`,
						`repeated sentence frame within ${WINDOW} sentences`
					)
				);
		}
	}
	let units = 0;
	for (let p = 0; p < k.length; p++) units += k[p] as number;
	return { k, e, evidence: ev, summary, features: { frameRepeats: units } };
}

export const st04: ChannelImpl = rateChannel(st04Def, nativeMeasure(measure));
export { st04Def as def, st04 as impl };
